export default class Glitcher
{
  static MAX_SIZE: number = 0.8;
  static EDGE_PADDING: number = 20;
  static TRANSITION_DURATION_MS: number = 1000;
  static BASE_FREQUENCY_MS: number = 15000;
  static TICKS_CHECK_DURATION_MS: number = 1000;
  static FALLBACK_TICKS_PER_MS: number = 15;
  static ZOOM_SPEED_PX_PER_S: number = 200;

  //elements
  private readonly canvas: HTMLCanvasElement;
  private readonly context: CanvasRenderingContext2D;
  private readonly images: HTMLImageElement[] = [];

  //device config
  private readonly devicePixelRatio: number = 1;
  private ticksPerMs: number|null = null; //60fps

  //resize listener
  private resizeTimeout: ReturnType<typeof setTimeout>|null = null;
  private readonly resizeObserver: ResizeObserver;
  private readonly handleResizeObserverCallback: ResizeObserverCallback;

  //listeners
  private readonly keyupHandler: EventListener;
  private readonly clickHandler: EventListener;
  private readonly loadHandler: EventListener;
  private readonly mousemoveHandler: EventListener;

  //canvas config
  private canvasWidthDpx: number = window.innerWidth;
  private canvasHeightDpx: number = window.innerHeight;

  private aimX: number = this.canvasWidthDpx / 2;
  private aimY: number = this.canvasHeightDpx / 2;

  private minX: number = 0;
  private maxX: number = this.canvasWidthDpx;
  private minY: number = 0;
  private maxY: number = this.canvasHeightDpx;

  private xAmplitudeDpx = (this.maxX - this.minX) / 2;
  private xFrequencyTicks = this.canvasHeightDpx / this.canvasWidthDpx * (Glitcher.BASE_FREQUENCY_MS / Glitcher.FALLBACK_TICKS_PER_MS);
  private xAxisDpx = this.canvasWidthDpx / 2;

  private yAmplitudeDpx = (this.maxY - this.minY) / 2;
  private yFrequencyTicks = this.canvasWidthDpx / this.canvasHeightDpx * (Glitcher.BASE_FREQUENCY_MS / Glitcher.FALLBACK_TICKS_PER_MS);
  private yAxisDpx = this.canvasHeightDpx / 2;

  //image config
  private image: HTMLImageElement|null = null;
  private imageWidthDpx: number = 0;
  private imageHeightDpx: number = 0;

  //animation state
  private initialized = false;
  private currentX: number;
  private currentY: number;
  private imageIndex: number = 0;
  private imageAlpha = 0;
  private ticks: number = 0;

  private isRunning: boolean = false;

  constructor(canvas: HTMLCanvasElement)
  {
    document.body.classList.add('is-loading');
    this.canvas = canvas;
    this.devicePixelRatio = window.devicePixelRatio || 1;

    const context = this.canvas.getContext('2d', { alpha: false });
    if(context instanceof CanvasRenderingContext2D) {
      this.context = context;
      this.context.scale(this.devicePixelRatio, this.devicePixelRatio);
    } else {
      throw new Error('Canvas context not found');
    }
    
    const images = Array.from(document.querySelectorAll('#canvas-src img')) as HTMLImageElement[];
    for (let i = images.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        var temp = images[i];
        images[i] = images[j];
        images[j] = temp;
    }
    this.images = images;

    this.image = this.images[0];

    this.aimX = this.canvasWidthDpx / 2;
    this.aimY = this.canvasHeightDpx / 2;

    this.currentX = this.canvasWidthDpx / 2;
    this.currentY = this.canvasHeightDpx / 2;
    this.updateImageSize();

    this.keyupHandler = this.handleKeyupEvent.bind(this);
    this.clickHandler = this.handleClickEvent.bind(this);
    this.loadHandler = this.handleLoadEvent.bind(this);
    this.mousemoveHandler = this.handleMouseMoveEvent.bind(this);
    this.handleResizeObserverCallback = this.resizeObserverCallback.bind(this);

    this.resizeObserver = new ResizeObserver(this.handleResizeObserverCallback);

    this.getTicksPerMs().then((ticksPerMs) => {
      this.ticksPerMs = ticksPerMs;
      this.updateCanvasSize();
      this.enable();
    });
  }

  private enable(): void
  {
    document.addEventListener('load', this.loadHandler, true);
    document.addEventListener('click', this.clickHandler, true);
    document.addEventListener('keyup', this.keyupHandler, true);
    document.addEventListener('mousemove', this.mousemoveHandler, false);
    this.resizeObserver.observe(document.body);

    if(this.image?.complete) {
      this.startDrawing();
    }
  }

  private startDrawing(): void
  {
    this.initialized = true;
    this.preDraw().then(() => {
      this.isRunning = true;
      document.body.classList.remove('is-loading');
      this.draw();
      this.canvas.style.removeProperty('transition');
      this.canvas.offsetWidth; //style recalc
      this.canvas.style.setProperty('opacity', '1');
    });
  }

  private tickFrameLoop()
  {
    if(this.ticksPerMs === null) { 
      requestAnimationFrame(() => {
        this.tickFrameLoop();
      })
      this.ticks++;
    }
  }

  private getTicksPerMs(): Promise<number>
  {
    return new Promise((resolve) => {

      const lastTicks = this.ticks;

      if(document.hidden) {
        resolve(Glitcher.FALLBACK_TICKS_PER_MS);
      }

      this.tickFrameLoop();

      setTimeout(() => {
        const ticksElapsed = this.ticks - lastTicks;
        const ticksPerMs = Math.max(
          Math.round(Glitcher.TICKS_CHECK_DURATION_MS / ticksElapsed),
          Glitcher.FALLBACK_TICKS_PER_MS
        );
        resolve(ticksPerMs);

      }, Glitcher.TICKS_CHECK_DURATION_MS);
    })
  }

  public destroy(): void
  {
    document.removeEventListener('load', this.loadHandler);
    document.removeEventListener('click', this.clickHandler);
    document.removeEventListener('keyup', this.keyupHandler);

    //stop requesting new animation frames
    this.isRunning = false;
  }

  private handleLoadEvent(event: Event): void
  {
    if(this.initialized === false) {
      this.startDrawing();
    }
  }

  private handleKeyupEvent(event: Event): void
  {
    if(event instanceof KeyboardEvent === false) {
      return;
    }

    switch(event.key) {
      case 'ArrowLeft':
        this.showPrev();
        break;
      case 'ArrowRight':
        this.showNext();
        break;
    }
  }

  private handleClickEvent(event: Event): void
  {
    this.showNext();
  }

  private handleMouseMoveEvent(event: Event): void
  {
    if(event instanceof MouseEvent === false) {
      return;
    }
    if(matchMedia('(pointer:coarse)').matches) {
      return;
    }

    var x = Math.round(event.clientX);  //x position within the element.
    var y = Math.round(event.clientY);  //y position within the element.

    this.aimX = x * this.devicePixelRatio;
    this.aimY = y * this.devicePixelRatio;
  }

  private showNext(): void
  {
    if(this.images[this.imageIndex+1]) {
      this.imageIndex++;
    } else {
      this.imageIndex = 0;
    }
    this.switchImage();
  }

  private showPrev(): void
  {
    if(this.images[this.imageIndex-1]) {
      this.imageIndex--;
    } else {
      this.imageIndex = this.images.length-1;
    }
    this.switchImage();
  }

  private switchImage(): Promise<boolean>
  {
    this.image = null;

    return new Promise((resolve) => {
      setTimeout(() => {
        this.imageAlpha = 0;
        this.currentX = this.canvasWidthDpx / 2 - this.imageWidthDpx / 2;
        this.currentY = this.canvasHeightDpx / 2 - this.imageHeightDpx / 2;
        
        this.image = this.images[this.imageIndex];
        this.image.sizes = this.imageWidthDpx * this.devicePixelRatio + 'px';
        this.image.loading = 'eager';
        this.updateImageSize();
        resolve(true);
      }, Glitcher.TRANSITION_DURATION_MS)
    })
  }

  private preDraw(): Promise<boolean>
  {
    return new Promise((resolve) => {

      this.context.save();

      this.canvas.style.setProperty('transition', 'none');
      this.canvas.offsetWidth; //style recalc
      this.canvas.style.setProperty('opacity', '0');

      setTimeout(() => {
        this.context.restore();
        resolve(true);
      }, 1000)
    })
  }

  private draw(): void
  {
    if(this.isRunning) {
      requestAnimationFrame(() => {
        this.draw()
      });
    }
    if(this.currentX && this.currentY && this.ticksPerMs) {

      // rotation oscilation
      this.context.translate(this.canvasWidthDpx, this.canvasHeightDpx);
      const rotationAmplitudeDeg = 1;
      const rotationAmplitudeRad = rotationAmplitudeDeg * Math.PI / 180;
      const rotationFrequencyTicks = this.ticksPerMs * Glitcher.BASE_FREQUENCY_MS * 1000;
      const rotationSin = Math.sin(this.ticks / rotationFrequencyTicks);
      const rotationRad = rotationSin * rotationAmplitudeRad / (Glitcher.BASE_FREQUENCY_MS * 1);
      this.context.rotate(rotationRad);
      this.context.translate(-1 * this.canvasWidthDpx, -1 * this.canvasHeightDpx);

      if(this.image && this.image.complete) {

        //apply alpha only on image
        if(this.imageAlpha < 1) {
          this.imageAlpha += 30 / Glitcher.TRANSITION_DURATION_MS;
          this.context.globalAlpha = this.imageAlpha;
        }

        this.context.drawImage(
          this.image,
          this.currentX,
          this.currentY,
          this.imageWidthDpx,
          this.imageHeightDpx
        );

        this.context.globalAlpha = 1;
      }

      this.ticks += 1;

      //mouse move modifier
      //get center of image to track relative movement from
      const imageCenterX = this.currentX + this.imageWidthDpx / 2;
      const imageCenterY = this.currentY + this.imageHeightDpx / 2;
      //movement modifier based on position of mouse relative to image center
      const modifierStrength = 1;
      const modifiedX = this.currentX + (this.aimX - imageCenterX) * modifierStrength;
      const modifiedY = this.currentY + (this.aimY - imageCenterY) * modifierStrength;

      //automatic movement
      const aimSinX = this.xAxisDpx + Math.sin(this.ticks / this.xFrequencyTicks) * this.xAmplitudeDpx - this.imageWidthDpx / 2;
      const aimSinY = this.yAxisDpx + Math.sin(this.ticks / this.yFrequencyTicks) * this.yAmplitudeDpx - this.imageHeightDpx / 2;
 
      this.currentX = Math.round(
        (aimSinX + (modifiedX - aimSinX) // difference between aim and current
        * 0.2) // times speed
        * 100) / 100; // round to two decimal places

      this.currentY = Math.round(
        (aimSinY + (modifiedY - aimSinY) // difference between aim and current
        * 0.2) // times speed
        * 100) / 100; // round to two decimal places
      
      const zoomAmountPx = Math.round(
        Glitcher.ZOOM_SPEED_PX_PER_S
        / Math.round(1000 / this.ticksPerMs) //px per tick
        * 10) / 10;

      // console.log(framePerSecond, pxPerFrame, zoomAmountPx);
      this.context.drawImage(
        this.canvas,
        -1 * zoomAmountPx,
        -1 * zoomAmountPx,
        this.canvasWidthDpx + 2 * zoomAmountPx,
        this.canvasHeightDpx + 2 * zoomAmountPx,
      );
    }
  }

  private resizeObserverCallback(entries: ResizeObserverEntry[]): void
  {
    this.isRunning = false;
    this.canvas.style.setProperty('opacity', '0');
    if(this.resizeTimeout) {
      clearTimeout(this.resizeTimeout)
    }
    this.resizeTimeout = setTimeout(() => {
      this.updateCanvasSize();
      this.updateImageSize();
      this.isRunning = true;
      this.draw();
      this.canvas.style.removeProperty('opacity');
    }, 500)
  }

  private updateImageSize(): void
  {
    if(this.image === null) {
      return;
    }
    
    const imageWidthDpx = this.image.width * this.devicePixelRatio;
    const imageHeightDpx = this.image.height * this.devicePixelRatio;
    const maxWidthDpx = Math.min(imageWidthDpx, this.canvasWidthDpx) * Glitcher.MAX_SIZE;
    const maxHeightDpx = Math.min(imageHeightDpx, this.canvasHeightDpx) * Glitcher.MAX_SIZE;
    const widthRatio = maxWidthDpx / imageWidthDpx;
    const heightRatio = maxHeightDpx / imageHeightDpx;
    const ratio = Math.min(widthRatio, heightRatio);

    this.imageWidthDpx = imageWidthDpx * ratio;
    this.imageHeightDpx = imageHeightDpx * ratio;

    //lpx
    this.image.sizes = Math.ceil(this.imageWidthDpx / this.devicePixelRatio) + 'px';

    const xPaddingDpx = Math.ceil(this.canvasWidthDpx / 100 * 5);
    const yPaddingDpx = Math.ceil(this.canvasHeightDpx / 100 * 5);

    const minXcanvas = xPaddingDpx;
    const minXimage = this.xAxisDpx - this.imageWidthDpx + xPaddingDpx;
    const maxXcanvas = this.canvasWidthDpx - this.imageWidthDpx - xPaddingDpx;
    const maxXimage = this.xAxisDpx - xPaddingDpx;

    this.minX = Math.ceil(Math.max(minXcanvas, minXimage));
    this.maxX = Math.floor(Math.min(maxXcanvas, maxXimage));

    const minYcanvas = yPaddingDpx;
    const minYimage = this.yAxisDpx - this.imageHeightDpx + yPaddingDpx;
    const maxYcanvas = this.canvasHeightDpx - this.imageHeightDpx - yPaddingDpx;
    const maxYimage = this.yAxisDpx - yPaddingDpx;

    this.minY = Math.ceil(Math.max(minYcanvas, minYimage));
    this.maxY = Math.floor(Math.min(maxYcanvas, maxYimage));

    this.xAmplitudeDpx = (this.maxX - this.minX) / 2;
    this.yAmplitudeDpx = (this.maxY - this.minY) / 2;

    this.image.loading = 'eager';
  }

  private updateCanvasSize(): void
  {
    const oldWidth = this.canvasWidthDpx
    const oldHeight = this.canvasHeightDpx;
    const offCanvas = new OffscreenCanvas(oldWidth, oldHeight);
    const offContext = offCanvas.getContext('2d');
    offContext?.drawImage(
      this.canvas,
      0, 0,
      oldWidth, oldHeight
    );    
    const newWidth = window.innerWidth * this.devicePixelRatio;
    const newHeight = window.innerHeight * this.devicePixelRatio;
    const newX = (newWidth - oldWidth) / 2;
    const newY = (newHeight - oldHeight) / 2

    //cache to avoid DOM access performance
    this.canvasWidthDpx = newWidth;
    this.canvasHeightDpx = newHeight;

    this.canvas.width = this.canvasWidthDpx;
    this.canvas.height = this.canvasHeightDpx;

    this.context.drawImage(
      offCanvas,
      newX, newY,
      oldWidth, oldHeight
    );

    if(this.ticksPerMs) {
      this.xFrequencyTicks = this.canvasHeightDpx / this.canvasWidthDpx * (Glitcher.BASE_FREQUENCY_MS / this.ticksPerMs);
      this.xAxisDpx = this.canvasWidthDpx / 2;

      this.yFrequencyTicks = this.canvasWidthDpx / this.canvasHeightDpx * (Glitcher.BASE_FREQUENCY_MS / this.ticksPerMs);
      this.yAxisDpx = this.canvasHeightDpx / 2;

      this.currentX = this.xAxisDpx - this.imageWidthDpx / 2;
      this.currentY = this.yAxisDpx / 2 - this.imageHeightDpx / 2;
    } 
  }

  public static getDevicePixelRatio(): number
  {
    var mediaQuery;
    var is_firefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
    if (window.devicePixelRatio !== undefined && !is_firefox) {
        return window.devicePixelRatio;
    } else if (window.matchMedia) {
        mediaQuery = "(-webkit-min-device-pixel-ratio: 1.5),\
          (min--moz-device-pixel-ratio: 1.5),\
          (-o-min-device-pixel-ratio: 3/2),\
          (min-resolution: 1.5dppx)";
        if (window.matchMedia(mediaQuery).matches) {
            return 1.5;
        }
        mediaQuery = "(-webkit-min-device-pixel-ratio: 2),\
          (min--moz-device-pixel-ratio: 2),\
          (-o-min-device-pixel-ratio: 2/1),\
          (min-resolution: 2dppx)";
        if (window.matchMedia(mediaQuery).matches) {
            return 2;
        }
        mediaQuery = "(-webkit-min-device-pixel-ratio: 0.75),\
          (min--moz-device-pixel-ratio: 0.75),\
          (-o-min-device-pixel-ratio: 3/4),\
          (min-resolution: 0.75dppx)";
        if (window.matchMedia(mediaQuery).matches) {
            return 0.7;
        }
        return 1;
    } else {
        return 1;
    }
  }
}